"use client";

import { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Button,
  Grid,
  createTheme,
  ThemeProvider,
  Autocomplete,
  Typography,
  Chip,
  alpha,
  Tooltip,
  Checkbox,
  CircularProgress,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PaymentsIcon from "@mui/icons-material/Payments";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import BarcodeIcon from "@mui/icons-material/QrCode";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChannelIcon from "@mui/icons-material/Tv";
import TuneIcon from "@mui/icons-material/Tune";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import CustomPagination from "./CustomPagination"; // Componente de paginación personalizado.
import { ErpContext } from "../../context/ErpContext";
import GraficasDetallesVentasModal from "./graficas/graficas-detalles-ventas-modal";
import ModalDetalleClienteGrupoDestino from "./graficas/modal-detalle-cliente-grupo-destino";
import ModalCrearActualizarEntidad from "../adminComponent/ModalCrearActualizarEntidad";
import ModalDocumentosAdjuntos from "../adminComponent/ModalDocumentosAdjuntos";
import { decodificarCodigoQR } from "../../utils/cuponeraUtils";
import { generarPdfClientePaqueteDetalle } from "../../context/pdf_esqueletos/clientePaqueteDetallePdf";
import { reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js";
import { formatPersonaApellidoNombre } from "../../utils/personaDisplay.js";

// Enhanced theme with more modern styling
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4a4a4e",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.08),
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "#fff",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            padding: "0 16px",
            height: "56px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "white !important",
            fontWeight: 600,
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "0 8px",
          },
          "& .MuiDataGrid-menuIconButton": {
            color: "rgba(255, 255, 255, 0.7) !important",
            "&:hover": {
              color: "white !important",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "white !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
            padding: "0 16px",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            },
            "&:hover": {
              backgroundColor: "rgba(66, 153, 225, 0.08)",
              transition: "background 0.2s ease",
            },
            transition: "background 0.2s ease",
            cursor: "pointer",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#fff",
            borderTop: "1px solid rgba(224, 224, 224, 0.5)",
            display: "none !important",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#a1a1a1",
            },
          },
        },
      },
    },
  },
});

const TablaDetallesVentas = () => {
  const {
    getClientePaqueteDetalle,
    getTransaccionItemByTipoTransaccionAllData,
    getEntidadesClientesTresLetras,
    getEntidadByID,
    getItemTresLetras,
    getItemById,
    itemTresLetras,
    entidadesClientesTresLetras,
    arrayItemTresLetras,
    getUbicacion,
    ubicaciones,
    isNavbarCollapsed,
    getParametrosGlobales,
    getCanalEntidad,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    getItemUbicacionFilter,
    getFechaHoraActualClient, // ← Función crítica para normalización con timezone
    user,
    tieneAccesoFuncionalidad,
  } = useContext(ErpContext);

  // Permisos granulares (CLIENTES3 - Cliente Paquete Detalle)
  const mostrarPrecioOriginalFooter = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_verPrecioOriginalFooter");
  const mostrarPrecioActualizadoFooter = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_verPrecioActualizadoFooter");
  const mostrarAbonadoFooter = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_verAbonadoFooter");
  const mostrarSaldoFooter = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_verSaldoFooter");
  const mostrarColumnaPrecioOriginal = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_columnaPrecioOriginal");
  const mostrarColumnaPrecioActualizado = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_columnaPrecioActualizado");
  const mostrarColumnaAbonado = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_columnaAbonado");
  const mostrarColumnaSaldo = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_columnaSaldo");
  const mostrarColumnaGanancia = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_columnaGanancia");
  const mostrarBtnVerDetalles = tieneAccesoFuncionalidad(user, "CLIENTES3", "CLIENTES3_btnVerDetalles");
  const [rows, setRows] = useState([]);
  const [filterCode, setFilterCode] = useState("");
  const [startDate, setStartDate] = useState(dayjs().startOf("year"));
  const [endDate, setEndDate] = useState(dayjs().endOf("year"));
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "fechaViaje", sort: "desc" }]);

  // Columnas ocultas por defecto (toggleables con 3 puntos).
  // Las de permiso solo se fuerzan ocultas cuando el usuario no tiene acceso.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    fechaViaje: false,
    nroPagos: false,
    dniCuitCuil: false,
    contacto: false,
    sexo: false,
    fechaNacimiento: false,
    observaciones: false,
  });
  
  // Estados para totales del footer
  const [totalPrecioOriginal, setTotalPrecioOriginal] = useState(0);
  const [totalPrecioActualizado, setTotalPrecioActualizado] = useState(0);
  const [totalAbonado, setTotalAbonado] = useState(0);
  const [totalSaldo, setTotalSaldo] = useState(0);
  const isFirstRender = useRef(true);
  const [searchText, setSearchText] = useState("");
  const [searchTextCliente, setSearchTextCliente] = useState("");
  const [dniCliente, setDniCliente] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedUbicacion, setSelectedUbicacion] = useState(null);
  const [ubicacionesFiltradas, setUbicacionesFiltradas] = useState([]);
  const [filtersActive, setFiltersActive] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [qrScanBuffer, setQrScanBuffer] = useState("");
  const qrScanTimeout = useRef(null);
  const qrProcesandoRef = useRef(false);
  const pendingEnterRef = useRef(false);
  const focusAttemptRef = useRef(0);
  const focusRetryTimeoutRef = useRef(null);

  const parseFechaViaje = (value) => {
    if (!value || typeof value !== "string") return null;
    const parts = value.split("/");
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map((p) => Number.parseInt(p, 10));
    if (!day || !month || !year) return null;
    const dateObj = new Date(year, month - 1, day);
    const time = dateObj.getTime();
    return Number.isNaN(time) ? null : time;
  };

  // Campos calculados en cliente: se ordenan en memoria (misma estrategia que Ver Balance)
  const CLIENT_COMPUTED_SORT_FIELDS = useMemo(
    () => new Set(["precioActualizado", "pagado", "saldo", "ganancia"]),
    []
  );

  const sortRowsByComputedField = (rowsToSort, field, direction) => {
    const multiplier = direction === "asc" ? 1 : -1;
    return [...rowsToSort].sort((a, b) => {
      const aValue = Number(a?.[field] ?? 0);
      const bValue = Number(b?.[field] ?? 0);
      return (aValue - bValue) * multiplier;
    });
  };

  const SORT_FIELD_LABELS = useMemo(
    () => ({
      fechaViaje: "Fecha Viaje",
      cliente: "Cliente",
      codigo: "Grupo",
      ubicacion: "Destino",
      precio: "Precio Original",
      precioActualizado: "Precio Actualizado",
      pagado: "Abonado",
      saldo: "Saldo",
      ganancia: "Ganancia",
    }),
    []
  );

  const buildQueryContext = async (
    pageToLoad = page,
    sortFieldOverride = null,
    sortOrderOverride = null
  ) => {
    let formattedStartDate = null;
    if (startDate) {
      const startDateStr = startDate.format("YYYY-MM-DD");
      const result1 = await getFechaHoraActualClient(startDateStr);
      formattedStartDate = dayjs(result1.fechaServidor).startOf("day").toDate();
    }

    let formattedEndDate = null;
    if (endDate) {
      const endDateStr = endDate.format("YYYY-MM-DD");
      const result2 = await getFechaHoraActualClient(endDateStr);
      formattedEndDate = dayjs(result2.fechaServidor).endOf("day").toDate();
    }

    const requestedSortField = sortFieldOverride ?? sortModel?.[0]?.field ?? "fechaViaje";
    const normalizedSortOrder = (sortOrderOverride ?? sortModel?.[0]?.sort) === "asc" ? "asc" : "desc";
    const isClientComputedSort = CLIENT_COMPUTED_SORT_FIELDS.has(requestedSortField);
    const backendSortField = isClientComputedSort ? "fechaViaje" : requestedSortField;

    const params = {
      idTipoTransaccion: "1,7",
      page: pageToLoad,
      limit,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      idEntidad: dniCliente?.id || null,
      idItem: selectedItem?.id || null,
      idUbicacion: selectedUbicacion?.id || null,
      canalEntidad: selectedCanalEntidad?.id || null,
      sortField: backendSortField,
      sortOrder: normalizedSortOrder,
    };

    return {
      params,
      requestedSortField,
      normalizedSortOrder,
      isClientComputedSort,
    };
  };

  const buildRowsWithCuotas = async (data = [], cuotasResumen = {}, descripcionItemsCache = null) => {
    const cache = descripcionItemsCache || new Map();
    const uniqueDescriptions = [...new Set(data.map((tItem) => tItem.item?.descripcion).filter(Boolean))];
    const missingDescriptions = uniqueDescriptions.filter((desc) => !cache.has(desc));

    if (missingDescriptions.length > 0) {
      const itemsPromises = missingDescriptions.map((desc) => getItemTresLetras(desc));
      const itemsResponses = await Promise.all(itemsPromises);
      missingDescriptions.forEach((desc, index) => {
        cache.set(desc, itemsResponses[index]?.items || []);
      });
    }

    const itemsCompletos = uniqueDescriptions.flatMap((desc) => cache.get(desc) || []);

    const flattenedRows = data.map((tItem) => {
      const baseDescripcion = tItem.item?.descripcion || "";
      const idItem = tItem.idItem;
      const itemCodigo = tItem.item?.codigo || "";
      const entidadTransaccion = tItem.Transaccion.entidad || {};

      let atributosStr = "";
      try {
        const activeAttributes = (itemAtributoNoEliminados || []).filter((attr) => !attr.eliminado);
        const itemCompleto = itemsCompletos.find(
          (it) =>
            it.id === idItem ||
            it.codigo === itemCodigo ||
            (it.descripcion && it.descripcion === baseDescripcion)
        );

        if (itemCompleto && activeAttributes.length > 0) {
          const attributeValues = activeAttributes
            .map((attr, index) => itemCompleto[`itemDatoAtributo${index + 1}`])
            .filter((v) => v && String(v).trim().length > 0);
          if (attributeValues.length > 0) {
            atributosStr = attributeValues.join(" ");
          }
        }
      } catch (e) {
        // Ignorar errores al formatear atributos
      }

      const descripcionConAtributos = atributosStr
        ? `${baseDescripcion} - ${atributosStr}`
        : baseDescripcion;

      const nombreCliente = formatPersonaApellidoNombre(entidadTransaccion);

      const contactoParts = [entidadTransaccion.telefono, entidadTransaccion.email].filter(
        (valor) => valor && String(valor).trim().length > 0
      );
      const contacto = contactoParts.length > 0 ? contactoParts.join(" | ") : "";

      const fechaNacimientoFormateada = entidadTransaccion.fechaNacimiento
        ? dayjs(entidadTransaccion.fechaNacimiento).format("DD/MM/YYYY")
        : null;

      return {
        id: `${tItem.Transaccion.id}-${idItem}`,
        ventaId: `${idItem}-${tItem.Transaccion.id}`,
        fecha: new Date(tItem.Transaccion.fecha).toLocaleDateString("es-AR"),
        fechaViaje: tItem.fechaInicioViaje
          ? new Date(tItem.fechaInicioViaje).toLocaleDateString("es-AR")
          : null,
        noches: tItem.nochesViaje || null,
        estadoViaje: tItem.estadoViaje || null,
        paqueteCancelado: tItem.paqueteCancelado || false,
        destinoViaje: tItem.destinoViaje || null,
        idItem,
        codigo: itemCodigo,
        codigoScanner: tItem.item?.codigoScanner || "",
        descripcion: descripcionConAtributos,
        precio: tItem.precio,
        montoTotal: tItem.montoTotal,
        porcentajeDescuento: tItem.porcentajeDescuento,
        ganancia: tItem.ganancia || 0,
        cliente: nombreCliente || entidadTransaccion.descripcion || "",
        dniCuitCuil: entidadTransaccion.dniCuitCuil || "",
        contacto,
        sexo: entidadTransaccion.sexo || "",
        fechaNacimiento: fechaNacimientoFormateada,
        observaciones: entidadTransaccion.observaciones || "",
        ubicacion: tItem.Transaccion.ubicacion?.descripcion || tItem.destinoViaje || "Sin destino",
        canal: tItem.Transaccion.entidad?.canalEntidad?.descripcion || "",
        idTransaccion: tItem.Transaccion.id,
        idEntidadNumero: tItem.Transaccion.entidad?.id || tItem.Transaccion.idEntidad,
        idUbicacion: tItem.Transaccion.ubicacion?.id || tItem.Transaccion.idUbicacion,
        entidadCompleta: {
          ...entidadTransaccion,
          descripcion: entidadTransaccion.descripcion,
          apellido: entidadTransaccion.apellido,
          telefono: entidadTransaccion.telefono,
          email: entidadTransaccion.email,
          direccion: entidadTransaccion.direccion,
          localidad: entidadTransaccion.localidad,
          provincia: entidadTransaccion.provincia,
          dniCuitCuil: entidadTransaccion.dniCuitCuil,
          sexo: entidadTransaccion.sexo,
          fechaNacimiento: entidadTransaccion.fechaNacimiento,
          observaciones: entidadTransaccion.observaciones,
        },
        tipoEntidadDescripcion:
          entidadTransaccion?.TipoEntidad?.descripcion ||
          entidadTransaccion?.tipoEntidad ||
          "Cliente",
      };
    });

    return flattenedRows.map((row) => {
      const resumenKey = `${row.idEntidadNumero}-${row.idItem}-${row.idUbicacion}`;
      const resumen = cuotasResumen[resumenKey] || {};
      const liberado = resumen.liberado === true;
      return {
        ...row,
        pagado: liberado ? 0 : resumen.pagado || 0,
        saldo: liberado ? 0 : resumen.saldo || 0,
        nroPagos: liberado ? 0 : resumen.nroPagos || 0,
        precioActualizado: liberado ? 0 : resumen.precioActualizado || 0,
        liberado,
      };
    });
  };

  const [usaCuponera, setUsaCuponera] = useState(true); // Default true
  const buscarButtonRef = useRef(null);
  const verDetallesButtonRef = useRef(null);
  const [shouldFocusVerDetalles, setShouldFocusVerDetalles] = useState(false);
  
  // Estados para canal de entidad
  const [canalesEntidades, setCanalesEntidades] = useState([])
  const [selectedCanalEntidad, setSelectedCanalEntidad] = useState(null)

  // Estados para gráficas
  const [openGraficasModal, setOpenGraficasModal] = useState(false);
  const [loadingGraficas, setLoadingGraficas] = useState(false);
  const [datosGraficas, setDatosGraficas] = useState([]);
  const [openModalVenta, setOpenModalVenta] = useState(false);
  const [selectedCuentaCorriente, setSelectedCuentaCorriente] = useState(null);
  const [openModalCliente, setOpenModalCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [openModalDocumentos, setOpenModalDocumentos] = useState(false);
  const [adjuntosDocumentos, setAdjuntosDocumentos] = useState(null);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  // Mantener mismo formato de etiqueta que TablaReceta (tabla paquetes)
  const generateGrupoLabel = (item) => {
    const activeAttributes = (itemAtributoNoEliminados || []).filter((attr) => !attr.eliminado)
    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item?.[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item?.codigo || ""} - ${item?.descripcion || ""} ${attributeValues} ${item?.codigoScanner || ""}`.trim()
  }

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setOpenModalVenta(false);
    setSelectedCuentaCorriente(null);
  };

  const handleOpenCreateClienteModal = () => {
    setSelectedCliente({
      id: null,
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: 1, // Tipo entidad = Cliente
    });
    setOpenModalCliente(true);
  };

  const handleCloseClienteModal = async (refresh = false) => {
    setOpenModalCliente(false);
    if (refresh) {
      await fetchData(page);
    }
  };

  const normalizeClienteForModal = (cliente, fallbackId) => {
    const base = cliente || { id: fallbackId };
    const fechaNacimiento = base?.fechaNacimiento
      ? (dayjs.isDayjs(base.fechaNacimiento) ? base.fechaNacimiento : dayjs(base.fechaNacimiento))
      : null;

    return {
      ...base,
      fechaNacimiento: fechaNacimiento && fechaNacimiento.isValid() ? fechaNacimiento : null,
    };
  };

  const abrirModalDesdeRow = (row) => {
    const entidadLite = row.entidadCompleta || {
      id: row.idEntidadNumero,
      descripcion: row.cliente,
    };

    const cuentaCorrienteLite = {
      entidadCompleta: entidadLite,
      tipoEntidad: row.tipoEntidadDescripcion || "Cliente",
      saldo: 0,
      // Datos para el tab de cuotas
      idEntidad: row.idEntidadNumero,
      idItem: row.idItem,
      idUbicacion: row.idUbicacion,
      paqueteCancelado: row.paqueteCancelado || false,
      precioOriginal: row.precio, // Precio original del paquete (sin descuentos ni devoluciones)
      transacciones: [
        {
          id: row.idTransaccion || (row.ventaId && Number(String(row.ventaId).split("-")[1])) || null,
          fecha: row.fecha,
          idTipoTransaccion: 1,
          montoTotal: row.montoTotal,
          descripcion: row.descripcion,
        },
      ],
      transaccionesItems: [
        {
          id: row.idTransaccion || null,
          fecha: row.fecha,
          TransaccionItems: [
            {
              idItem: row.idItem,
              item: { descripcion: row.descripcion },
              cantidad: 0,
              precio: row.precio,
              montoTotal: row.montoTotal,
            },
          ],
        },
      ],
    };
    setSelectedCuentaCorriente(cuentaCorrienteLite);
    setOpenModalVenta(true);
  };

  const handleAbrirDocumentos = async (row) => {
    setOpenModalDocumentos(true);
    setLoadingDocumentos(true);
    try {
      const entidad = await getEntidadByID(row.idEntidadNumero);
      setAdjuntosDocumentos(entidad?.adjuntos || null);
    } catch (error) {
      console.error("Error al cargar documentos adjuntos:", error);
      setAdjuntosDocumentos(null);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // Función para procesar el escaneo de QR de cuponera
  const procesarQRCuponera = async (qrData) => {
    console.log('🔍 Procesando QR escaneado:', qrData);

    qrProcesandoRef.current = true;
    pendingEnterRef.current = false;
    
    const datosQR = decodificarCodigoQR(qrData);
    
    if (!datosQR) {
      console.warn('⚠️ QR no válido o no corresponde a una cuponera');
      return;
    }

    console.log('✅ QR decodificado:', datosQR);

    // Buscar y seleccionar el cliente
    let clienteEncontrado = entidadesClientesTresLetras.find(
      c => c.id === datosQR.idCliente
    );

    if (!clienteEncontrado) {
      const clienteResponse = await getEntidadByID(datosQR.idCliente);
      clienteEncontrado = Array.isArray(clienteResponse) ? clienteResponse[0] : clienteResponse;
    }

    if (clienteEncontrado) {
      const clienteSeleccionado = {
        label: `${clienteEncontrado.descripcion || ''} ${clienteEncontrado.apellido || ''} ${clienteEncontrado.dniCuitCuil || ''}`.trim(),
        descripcion: clienteEncontrado.descripcion || null,
        dniCuitCuil: clienteEncontrado.dniCuitCuil,
        id: clienteEncontrado.id,
        apellido: clienteEncontrado.apellido,
        telefono: clienteEncontrado.telefono || null,
        email: clienteEncontrado.email || null,
        direccion: clienteEncontrado.direccion || null,
        localidad: clienteEncontrado.localidad || null,
        provincia: clienteEncontrado.provincia || null,
        idTipoEntidad: clienteEncontrado.idTipoEntidad,
      };
      setDniCliente(clienteSeleccionado);
      // También actualizar el texto de búsqueda para que se vea en el autocomplete
      setSearchTextCliente(clienteSeleccionado.label);
      console.log('👤 Cliente seleccionado:', clienteSeleccionado.label);
    }

    // Buscar y seleccionar el grupo (destino/item)
    let grupoEncontrado = (itemTresLetras || []).find(
      it => it.id === datosQR.idDestino
    );

    if (!grupoEncontrado) {
      const itemResponse = await getItemById(datosQR.idDestino);
      grupoEncontrado = Array.isArray(itemResponse) ? itemResponse[0] : itemResponse;
    }

    if (grupoEncontrado) {
      const itemSeleccionado = {
        label: generateGrupoLabel(grupoEncontrado),
        descripcion: grupoEncontrado.descripcion,
        codigo: grupoEncontrado.codigo,
        id: grupoEncontrado.id,
        codigoScanner: grupoEncontrado.codigoScanner,
      };
      setSelectedItem(itemSeleccionado);
      console.log('📍 Grupo seleccionado:', itemSeleccionado.label);

      // Buscar y seleccionar la ubicación (paquete)
      let ubicacionesDisponibles = ubicaciones;
      if (!ubicacionesDisponibles || ubicacionesDisponibles.length === 0) {
        ubicacionesDisponibles = await getUbicacion();
      }

      const ubicacionEncontrada = (ubicacionesDisponibles || []).find(
        u => u.id === datosQR.idUbicacion
      );

      if (ubicacionEncontrada) {
        const ubicacionSeleccionada = {
          id: ubicacionEncontrada.id,
          label: ubicacionEncontrada.descripcion
        };
        setSelectedUbicacion(ubicacionSeleccionada);
        console.log('🎯 Ubicación seleccionada:', ubicacionSeleccionada.label);
      }
    }

    // Marcar filtros como activos
    setFiltersActive(true);

    // Llevar el foco al botón Buscar para que el Enter dispare la búsqueda
    setTimeout(() => {
      buscarButtonRef.current?.focus();
    }, 150);

    qrProcesandoRef.current = false;
    if (pendingEnterRef.current) {
      pendingEnterRef.current = false;
      handleBuscar();
    }
  };

  // Columnas generadas dinámicamente usando useMemo
  const columns = useMemo(() => {
    const baseColumns = [
    // 1. Fecha de viaje
    {
      field: "fechaViaje",
      headerName: "Fecha Viaje",
      headerAlign: "center",
      align: "left",
      flex: 0.9,
      minWidth: 120,
      sortComparator: (v1, v2) => {
        const t1 = parseFechaViaje(v1);
        const t2 = parseFechaViaje(v2);
        if (t1 == null && t2 == null) return 0;
        if (t1 == null) return -1;
        if (t2 == null) return 1;
        return t1 - t2;
      },
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Chip
            icon={<CalendarTodayIcon fontSize="small" />}
            label={params.value || "Sin fecha"}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: alpha("#3B82F6", 0.1),
              color: "#3B82F6",
              "& .MuiChip-icon": { color: "#3B82F6" },
            }}
          />
        </Box>
      ),
    },
    // 2. Cliente
    {
      field: "cliente",
      headerName: "Cliente",
      headerAlign: "center",
      align: "left",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "100%" }}>
          <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    // 2.1 DNI/CUIT/CUIL (oculta por defecto)
    {
      field: "dniCuitCuil",
      headerName: "DNI/CUIT/CUIL",
      headerAlign: "center",
      align: "left",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Tooltip title={params.value || ""}>
            <Typography variant="body2" noWrap>
              {params.value || "-"}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    // 2.2 Contacto (oculta por defecto)
    {
      field: "contacto",
      headerName: "Contacto",
      headerAlign: "center",
      align: "left",
      flex: 1.3,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Tooltip title={params.value || ""}>
            <Typography variant="body2" noWrap>
              {params.value || "-"}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    // 2.3 Sexo (oculta por defecto)
    {
      field: "sexo",
      headerName: "Sexo",
      headerAlign: "center",
      align: "left",
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Typography variant="body2" noWrap>
            {params.value || "-"}
          </Typography>
        </Box>
      ),
    },
    // 2.4 Fecha Nacimiento (oculta por defecto)
    {
      field: "fechaNacimiento",
      headerName: "Fecha Nacimiento",
      headerAlign: "center",
      align: "left",
      flex: 0.9,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Typography variant="body2" noWrap>
            {params.value || "-"}
          </Typography>
        </Box>
      ),
    },
    // 2.5 Observaciones (oculta por defecto)
    {
      field: "observaciones",
      headerName: "Observaciones",
      headerAlign: "center",
      align: "left",
      flex: 1.6,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Tooltip title={params.value || ""}>
            <Typography variant="body2" noWrap>
              {params.value || "-"}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    // 3. Grupo
    {
      field: "codigo",
      headerName: "Grupo",
      headerAlign: "center",
      align: "left",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              borderColor: "rgb(244, 147, 14)",
              color: "rgb(244, 147, 14)",
            }}
          />
        </Box>
      ),
    },
    // 4. Destino
    {
      field: "ubicacion",
      headerName: "Destino",
      headerAlign: "center",
      align: "left",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "100%" }}>
          <LocationOnIcon
            fontSize="small"
            sx={{ color: alpha("#5b5b5f", 0.7) }}
          />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    // 5. Liberado
    {
      field: "liberado",
      headerName: "Liberado",
      headerAlign: "center",
      align: "center",
      sortable: false,
      width: 100,
      flex: 0.6,
      renderCell: (params) => (
        <Tooltip title={params.value ? "Pasajero liberado (no paga)" : "Pasajero regular"}>
          <Checkbox
            checked={params.value || false}
            disabled
            size="small"
            sx={{
              p: 0.5,
              color: params.value ? "success.main" : "action.disabled",
              "&.Mui-checked": {
                color: "success.main",
              },
              "&.Mui-disabled": {
                opacity: 1,
              },
            }}
          />
        </Tooltip>
      ),
    },
    // 6. Precio Original
    {
      field: "precio",
      headerName: "Precio Original",
      hide: false,
      headerAlign: "center",
      align: "right",
      type: "number",
      width: 130,
      flex: 0.9,
      renderCell: (params) => (
        <Tooltip title="Precio base del grupo sin descuentos ni ajustes individuales">
          <span style={{ color: "#718096", fontWeight: "bold" }}>
            ${params.value?.toLocaleString("es-AR") || "0.00"}
          </span>
        </Tooltip>
      ),
    },
    // 7. Precio Actualizado
    {
      field: "precioActualizado",
      headerName: "Precio Actualizado",
      hide: false,
      headerAlign: "center",
      align: "right",
      type: "number",
      width: 150,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="Precio real considerando cuotas pagadas e intereses aplicados en cuotas vencidas">
          <span style={{ color: "#4CAF50", fontWeight: "bold" }}>
            ${params.value?.toLocaleString("es-AR") || "0.00"}
          </span>
        </Tooltip>
      ),
    },
    // 8. Abonado
    {
      field: "pagado",
      headerName: "Abonado",
      hide: false,
      headerAlign: "center",
      align: "right",
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <span style={{ color: "#4CAF50", fontWeight: "bold" }}>
          ${params.value?.toLocaleString("es-AR") || "0.00"}
        </span>
      ),
    },
    // 9. Saldo
    {
      field: "saldo",
      headerName: "Saldo",
      hide: false,
      headerAlign: "center",
      align: "right",
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <span style={{ color: params.value > 0 ? "#e53e3e" : "#4CAF50", fontWeight: "bold" }}>
          ${params.value?.toLocaleString("es-AR") || "0.00"}
        </span>
      ),
    },
    // 10. Ganancia
    {
      field: "ganancia",
      headerName: "Ganancia",
      hide: false,
      headerAlign: "center",
      align: "right",
      sortingOrder: ["desc", "asc"],
      type: "number",
      width: 120,
      flex: 0.8,
      renderCell: (params) => {
        const ganancia = params.value || 0;
        const color = ganancia >= 0 ? "#3182ce" : "#e53e3e";
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, width: "100%" }}>
            <AccountBalanceWalletIcon fontSize="small" sx={{ color }} />
            <Typography variant="body2" sx={{ fontWeight: "600", color }}>
              ${ganancia.toLocaleString("es-AR") || "0.00"}
            </Typography>
          </Box>
        );
      },
    },
    // 10. Nro de Pagos
    {
      field: "nroPagos",
      headerName: "Nro de Pagos",
      headerAlign: "center",
      align: "right",
      sortable: false,
      type: "number",
      width: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <Chip
            label={params.value || 0}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: alpha("#3182ce", 0.1),
              color: "#3182ce",
            }}
          />
        </Box>
      ),
    },
    // 11. Acciones
    {
      field: "acciones",
      headerName: "Acciones",
      hide: false,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {mostrarBtnVerDetalles && (
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              ref={rows.length === 1 ? verDetallesButtonRef : null}
              onClick={() => abrirModalDesdeRow(params.row)}
              onKeyDown={(event) => {
                const isEnterKey =
                  event.key === "Enter" ||
                  event.key === "NumpadEnter" ||
                  event.code === "Enter" ||
                  event.code === "NumpadEnter";

                if (isEnterKey) {
                  event.preventDefault();
                  abrirModalDesdeRow(params.row);
                }
              }}
              sx={{
                color: alpha("#5b5b5f", 0.9),
                "&:hover": { backgroundColor: alpha("#5b5b5f", 0.1) },
              }}
            >
              <PaymentsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          )}
          <Tooltip title="Documentos adjuntos">
            <IconButton
              size="small"
              onClick={() => handleAbrirDocumentos(params.row)}
              sx={{
                color: alpha("#5b5b5f", 0.9),
                "&:hover": { backgroundColor: alpha("#5b5b5f", 0.1) },
              }}
            >
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Oculta columna "Canal"
  // if (canalesEntidades && canalesEntidades.length > 0) {
  //   baseColumns.splice(-1, 0, {
  //     field: "canal",
  //     headerName: "Canal",
  //     headerAlign: "center",
  //     flex: 1,
  //     minWidth: 120,
  //     renderCell: (params) => (
  //       <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //         <ChannelIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
  //         <Typography variant="body2">{params.value || "Sin canal"}</Typography>
  //       </Box>
  //     ),
  //   });
  // }

  return baseColumns;
}, [canalesEntidades, rows.length]);

  // Función para realizar la consulta usando paginado y filtros.
  const fetchData = async (pageToLoad = page, sortFieldOverride = null, sortOrderOverride = null) => {
    try {
      setLoading(true);
      const queryContext = await buildQueryContext(pageToLoad, sortFieldOverride, sortOrderOverride);
      const response = await getClientePaqueteDetalle(queryContext.params);

      if (response) {
        const { data, total, totalPages: tPages, currentPage, cuotasResumen = {} } = response;
        const rowsWithCuotas = await buildRowsWithCuotas(data, cuotasResumen);

        const rowsFinales = queryContext.isClientComputedSort
          ? sortRowsByComputedField(
              rowsWithCuotas,
              queryContext.requestedSortField,
              queryContext.normalizedSortOrder
            )
          : rowsWithCuotas;

        const totalSum = rowsFinales.reduce(
          (sum, row) => sum + (row.montoTotal || 0),
          0
        );
        setTotalAmount(totalSum);

        const sumaPrecioOriginal = rowsFinales.reduce((sum, row) => sum + (row.precio || 0), 0);
        const sumaPrecioActualizado = rowsFinales.reduce((sum, row) => sum + (row.precioActualizado || 0), 0);
        const sumaAbonado = rowsFinales.reduce((sum, row) => sum + (row.pagado || 0), 0);
        const sumaSaldo = rowsFinales.reduce((sum, row) => sum + (row.saldo || 0), 0);

        setTotalPrecioOriginal(sumaPrecioOriginal);
        setTotalPrecioActualizado(sumaPrecioActualizado);
        setTotalAbonado(sumaAbonado);
        setTotalSaldo(sumaSaldo);

        setRows(rowsFinales);
        setTotalCount(total);
        setTotalPages(tPages);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (loading || isGeneratingPdf) return;
    const deliveryContext = { targetWindow: reservePdfTarget() }

    try {
      setIsGeneratingPdf(true);

      const queryContext = await buildQueryContext(1);
      const firstResponse = await getClientePaqueteDetalle(queryContext.params);

      if (!firstResponse || !Array.isArray(firstResponse.data) || firstResponse.data.length === 0) {
        return;
      }

      const descripcionItemsCache = new Map();
      let allRows = await buildRowsWithCuotas(
        firstResponse.data,
        firstResponse.cuotasResumen || {},
        descripcionItemsCache
      );

      const totalPagesForPdf = Number(firstResponse.totalPages || 1);
      for (let currentPage = 2; currentPage <= totalPagesForPdf; currentPage += 1) {
        const pageResponse = await getClientePaqueteDetalle({
          ...queryContext.params,
          page: currentPage,
        });

        if (!pageResponse || !Array.isArray(pageResponse.data) || pageResponse.data.length === 0) {
          continue;
        }

        const pageRows = await buildRowsWithCuotas(
          pageResponse.data,
          pageResponse.cuotasResumen || {},
          descripcionItemsCache
        );
        allRows = allRows.concat(pageRows);
      }

      const rowsFinales = queryContext.isClientComputedSort
        ? sortRowsByComputedField(
            allRows,
            queryContext.requestedSortField,
            queryContext.normalizedSortOrder
          )
        : allRows;

      if (rowsFinales.length === 0) return;

      const activeSort = sortModel?.[0];
      const sortLabel = activeSort?.field
        ? SORT_FIELD_LABELS[activeSort.field] || activeSort.field
        : "Fecha Viaje";
      const sortDirection = activeSort?.sort === "asc" ? "ASC" : "DESC";

      let logoBase64 = null
      try {
        const params = await getParametrosGlobales(["logoNavbarTenant"])
        logoBase64 = params?.logoNavbarTenant || null
      } catch (e) { /* ignorar */ }

      generarPdfClientePaqueteDetalle({
        rows: rowsFinales,
        filtros: {
          fechaDesde: startDate ? startDate.format("DD/MM/YYYY") : null,
          fechaHasta: endDate ? endDate.format("DD/MM/YYYY") : null,
          cliente: dniCliente?.label || null,
          grupo: selectedItem?.label || null,
          destino: selectedUbicacion?.label || null,
          canal: selectedCanalEntidad?.label || selectedCanalEntidad?.descripcion || null,
          orden: `${sortLabel} (${sortDirection})`,
        },
        logoBase64,
        deliveryContext,
      });
    } catch (error) {
      console.error("Error al exportar PDF de Cliente Grupo:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleSortModelChange = (newSortModel) => {
    const clickedField =
      Array.isArray(newSortModel) && newSortModel.length > 0
        ? newSortModel[0]?.field
        : "fechaViaje";
    const nextSort = Array.isArray(newSortModel) && newSortModel.length > 0 ? newSortModel[0]?.sort : "desc";
    const normalizedSort = nextSort === "asc" ? "asc" : "desc";
    const nextSortModel = [{ field: clickedField, sort: normalizedSort }];

    setSortModel(nextSortModel);

    if (CLIENT_COMPUTED_SORT_FIELDS.has(clickedField)) {
      setRows((prevRows) => sortRowsByComputedField(prevRows, clickedField, normalizedSort));
      return;
    }

    setPage(1);
    fetchData(1, clickedField, normalizedSort);
  };

  // Al montar el componente se realiza la consulta inicial.
  useEffect(() => {
    const fetchDataInicio = async () => {
      try {
        await getUbicacion();
        await getItemAtributoNoEliminados();
        
        // Cargar canales de entidad si está habilitado
        const canalEntidad = await getParametrosGlobales(["canalEntidad"])
        console.log("canalEntidad canalEntidad :", canalEntidad.canalEntidad)
        if (canalEntidad.canalEntidad == 1) {
          const datosCanalesEntidades = await getCanalEntidad()
          console.log("datosCanalesEntidades:", datosCanalesEntidades)
          setCanalesEntidades(datosCanalesEntidades)
        }
        
        // Cargar parámetro de cuponera
        const parametrosCuponera = await getParametrosGlobales(["usaCuponera"])
        setUsaCuponera(
          parametrosCuponera.usaCuponera === '1' || 
          parametrosCuponera.usaCuponera === 1 || 
          parametrosCuponera.usaCuponera === 'true' || 
          parametrosCuponera.usaCuponera === true
        );
        
        fetchData(1);
      } catch (error) {
        console.error("Error al cargar datos iniciales:", error)
      }
    };
    
    fetchDataInicio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1); // Llama a la API
    } else {
      setFilteredItems([]); // Vacía las opciones si hay menos de 3 letras
    }
  }, [searchTextCliente]); //Este paramatro searchText condiciona al useEffect, detectando sus cambios. Solo se ejecuta si cambia el valor del input de searchText.

  // Efecto para búsqueda de ítems (soloGrupos: solo grupos/paquetes, esServicio=false)
  useEffect(() => {
    if (searchText.length >= 3) {
      arrayItemTresLetras([]);
      getItemTresLetras(searchText, true);
    } else {
      arrayItemTresLetras([]);
      setFilteredItems([]);
    }
  }, [searchText]);

  // Cargar ubicaciones filtradas cuando se selecciona un grupo
  useEffect(() => {
    const cargarUbicacionesFiltradas = async () => {
      if (selectedItem?.id) {
        try {
          const ubicacionesData = await getItemUbicacionFilter(selectedItem.id, true);
          
          // Mapear las ubicaciones recibidas
          const ubicacionesMapeadas = ubicacionesData.map((itemUbic) => ({
            id: itemUbic.ubicacion.id,
            descripcion: itemUbic.ubicacion.descripcion,
          }));
          
          setUbicacionesFiltradas(ubicacionesMapeadas);
        } catch (error) {
          console.error("Error al cargar ubicaciones filtradas:", error);
          setUbicacionesFiltradas([]);
        }
      } else {
        setUbicacionesFiltradas([]);
      }
    };

    cargarUbicacionesFiltradas();
  }, [selectedItem?.id]);

  // Cuando cambie la página (por la UI de paginado), se vuelve a consultar.
  useEffect(() => {
    if (!isFirstRender.current) {
      fetchData(page);
    } else {
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Enfocar el botón de acción cuando la búsqueda deja un único resultado
  useEffect(() => {
    if (!shouldFocusVerDetalles || loading) return;

    if (rows.length !== 1) {
      setShouldFocusVerDetalles(false);
      focusAttemptRef.current = 0;
      return;
    }

    const intentarFocus = () => {
      if (verDetallesButtonRef.current) {
        verDetallesButtonRef.current.focus();
        setShouldFocusVerDetalles(false);
        focusAttemptRef.current = 0;
        return;
      }

      if (focusAttemptRef.current >= 10) {
        setShouldFocusVerDetalles(false);
        focusAttemptRef.current = 0;
        return;
      }

      focusAttemptRef.current += 1;
      focusRetryTimeoutRef.current = setTimeout(intentarFocus, 100);
    };

    intentarFocus();

    return () => {
      if (focusRetryTimeoutRef.current) {
        clearTimeout(focusRetryTimeoutRef.current);
      }
    };
  }, [rows, loading, shouldFocusVerDetalles]);

  // Verificar si hay filtros activos
  useEffect(() => {
    const hasActiveFilters =
      (endDate && startDate && !endDate.isSame(startDate, "day")) ||
      dniCliente ||
      selectedItem ||
      selectedUbicacion;

    setFiltersActive(hasActiveFilters);
  }, [startDate, endDate, dniCliente, selectedItem, selectedUbicacion]);

  // Al presionar el botón "Buscar" se resetea a la página 1 y se ejecuta el fetch.
  const handleBuscar = () => {
    focusAttemptRef.current = 0;
    setShouldFocusVerDetalles(true);
    setPage(1);
    fetchData(1);
  };

  const handleEnterOnFiltros = (event) => {
    const isEnterKey =
      event.key === "Enter" ||
      event.key === "NumpadEnter" ||
      event.code === "Enter" ||
      event.code === "NumpadEnter";

    if (!isEnterKey) return;

    const target = event.target;
    const isAutocompleteOpen = target?.getAttribute?.("aria-expanded") === "true";
    const isButton = target?.tagName === "BUTTON";
    const isOption = target?.getAttribute?.("role") === "option";

    if (qrProcesandoRef.current) {
      pendingEnterRef.current = true;
      event.preventDefault();
      return;
    }

    if (isAutocompleteOpen || isButton || isOption) {
      return;
    }

    event.preventDefault();
    handleBuscar();
  };

  // Función para abrir el modal de gráficas
  const handleOpenGraficasModal = async () => {
    setLoadingGraficas(true);
    setOpenGraficasModal(true);

    try {
      // Normalizar fechas con timezone antes de enviar
      let formattedStartDate = null;
      if (startDate) {
        const startDateStr = startDate.format("YYYY-MM-DD");
        const result1 = await getFechaHoraActualClient(startDateStr);
        formattedStartDate = dayjs(result1.fechaServidor).startOf("day").toDate();
      }
      
      let formattedEndDate = null;
      if (endDate) {
        const endDateStr = endDate.format("YYYY-MM-DD");
        const result2 = await getFechaHoraActualClient(endDateStr);
        formattedEndDate = dayjs(result2.fechaServidor).endOf("day").toDate();
      }
      
      const params = {
        idTipoTransaccion: "1,7", // Ventas - mismo filtro que la tabla principal
        startDate: formattedStartDate, // ← Date object con timezone
        endDate: formattedEndDate,     // ← Date object con timezone
        idEntidad: dniCliente?.id || null,
        idItem: selectedItem?.id || null,
        idUbicacion: selectedUbicacion?.id || null,
        canalEntidad: selectedCanalEntidad?.id || null,
      };

      // Llamar a la nueva función SIN PAGINADO para obtener todos los datos
      const allData = await getTransaccionItemByTipoTransaccionAllData(params);
      console.log("allData ", allData)

      setDatosGraficas(allData.data);
    } catch (error) {
      console.error("Error al cargar datos para gráficas:", error);
      setDatosGraficas([]);
    } finally {
      setLoadingGraficas(false);
    }
  };

  // Función para cerrar el modal de gráficas
  const handleCloseGraficasModal = () => {
    setOpenGraficasModal(false);
    setDatosGraficas([]);
  };

  // Resetear filtros
  const resetFilters = () => {
    setDniCliente(null);
    setSelectedItem(null);
    setSelectedUbicacion(null);
    setSearchText("");
    setSearchTextCliente("");
    setSelectedCanalEntidad(null);

    // Resetear fechas: desde el 1 de enero hasta el 31 de diciembre del año actual
    setStartDate(dayjs().startOf("year"));
    setEndDate(dayjs().endOf("year"));

    // Refrescar datos con filtros reseteados
    setPage(1);
    fetchData(1);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#f8f9fa",
          padding: "16px 16px 5px 16px",
          gap: "16px 16px 5px 16px",
        }}
      >

        {/* Título: Gestión de Clientes (igual que en Ver clientes) */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <BusinessIcon
            sx={{
              fontSize: 28,
              color: "rgb(244, 147, 14)",
              mr: 1.5,
            }}
          />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Gestión de Clientes
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Contenedor de filtros - Diseño mejorado */}
        <Paper
          elevation={0}
          onKeyDown={handleEnterOnFiltros}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            {/* Fecha Viaje Desde */}
            <Grid item xs="auto" sx={{ minWidth: 150 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Fecha Viaje Desde"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Fecha Viaje Hasta */}
            <Grid item xs="auto" sx={{ minWidth: 150 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Fecha Viaje Hasta"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Cliente */}
            <Grid item xs sx={{ minWidth: 180 }}>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                options={entidadesClientesTresLetras.map((it) => ({
                  label: `${it.descripcion} ${it.apellido} ${it.dniCuitCuil}`,
                  descripcion: it.descripcion || null,
                  dniCuitCuil: it.dniCuitCuil,
                  id: it.id,
                  apellido: it.apellido,
                  telefono: it.telefono || null,
                  email: it.email || null,
                  direccion: it.direccion || null,
                  localidad: it.localidad || null,
                  provincia: it.provincia || null,
                  idTipoEntidad: it.idTipoEntidad,
                }))}
                value={dniCliente}
                onChange={(event, newValue) => setDniCliente(newValue)}
                onInputChange={(event, newInputValue) => {
                  setSearchTextCliente(newInputValue);
                  
                  // Detectar QR de cuponera
                  // Soporta formato nuevo (G-25_C-8) y legacy (G:25|C:8)
                  const esQRNuevo = newInputValue && newInputValue.includes('G-') && newInputValue.includes('_');
                  const esQRLegacy = newInputValue && newInputValue.includes('G:') && newInputValue.includes('|');
                  
                  if (esQRNuevo || esQRLegacy) {
                    // QR completo detectado - procesar inmediatamente
                    console.log('[QR] Detectado QR completo:', newInputValue);
                    procesarQRCuponera(newInputValue);
                    setSearchTextCliente('');
                    setQrScanBuffer('');
                  } else if (newInputValue && (newInputValue.startsWith('G-') || newInputValue.startsWith('G:'))) {
                    // Posible inicio de QR (scanner escribiendo por partes)
                    setQrScanBuffer(prev => prev + newInputValue);
                    
                    if (qrScanTimeout.current) {
                      clearTimeout(qrScanTimeout.current);
                    }
                    
                    qrScanTimeout.current = setTimeout(() => {
                      const bufferCompleto = qrScanBuffer + newInputValue;
                      const bufferEsQRNuevo = bufferCompleto.includes('G-') && bufferCompleto.includes('_');
                      const bufferEsQRLegacy = bufferCompleto.includes('G:') && bufferCompleto.includes('|');
                      
                      if (bufferEsQRNuevo || bufferEsQRLegacy) {
                        console.log('[QR] QR completo desde buffer:', bufferCompleto);
                        procesarQRCuponera(bufferCompleto);
                        setQrScanBuffer('');
                        setSearchTextCliente('');
                      }
                    }, 100);
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={usaCuponera ? "Buscar Cliente / Escanear QR" : "Buscar Cliente"} 
                    placeholder={usaCuponera ? "Nombre, DNI o escanee código QR" : "Nombre o DNI"}
                  />
                )}
              />
            </Grid>

            {/* Buscar Grupo */}
            <Grid item xs sx={{ minWidth: 150 }}>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                options={(itemTresLetras || []).map((it) => ({
                  label: generateGrupoLabel(it),
                  descripcion: it.descripcion,
                  codigo: it.codigo,
                  id: it.id,
                  codigoScanner: it.codigoScanner,
                }))}
                value={selectedItem}
                onChange={(event, newValue) => {
                  setSelectedItem(newValue)
                  setSelectedUbicacion(null)
                }}
                onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Grupo"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon color="action" sx={{ ml: 0.5, mr: 0 }} fontSize="small" />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Destino */}
            <Grid item xs sx={{ minWidth: 150 }}>
              <Autocomplete
                size="small"
                fullWidth
                disablePortal
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                options={
                  selectedItem && ubicacionesFiltradas.length > 0
                    ? ubicacionesFiltradas.map((it) => ({ id: it.id, label: it.descripcion }))
                    : ubicaciones.filter((it) => it.esDestino === true).map((it) => ({ id: it.id, label: it.descripcion }))
                }
                value={selectedUbicacion}
                onChange={(event, newValue) => setSelectedUbicacion(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar Destino"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <LocationOnIcon color="action" sx={{ ml: 0.5, mr: 0 }} fontSize="small" />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Botón Buscar */}
            <Grid item xs="auto">
              <Button
                variant="contained"
                onClick={handleBuscar}
                startIcon={<SearchIcon />}
                ref={buscarButtonRef}
                disabled={loading}
                size="small"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#5b5b5f",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: alpha("#5b5b5f", 0.9) },
                }}
              >
                Buscar
              </Button>
            </Grid>

            {/* Botón Limpiar filtros (icono genérico) */}
            <Grid item xs="auto">
              <Tooltip title="Limpiar filtros">
                <span>
                  <IconButton
                    onClick={resetFilters}
                    disabled={!filtersActive}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      "&.MuiIconButton-root": { borderRadius: 0.7 },
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>

            {/* Botón + Crear */}
            <Grid item xs="auto">
              <Button
                variant="contained"
                onClick={handleOpenCreateClienteModal}
                startIcon={<AddCircleOutlineIcon fontSize="small" />}
                size="small"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#5b5b5f",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: alpha("#5b5b5f", 0.9) },
                }}
              >
                Crear
              </Button>
            </Grid>

            {/* Botón PDF */}
            <Grid item xs="auto">
              <Tooltip title={isGeneratingPdf ? "Generando PDF..." : "PDF"}>
                <span>
                  <IconButton
                    onClick={handleExportPdf}
                    disabled={loading || isGeneratingPdf}
                    size="small"
                    sx={{
                      color: alpha("#5b5b5f", 0.9),
                      "&:hover": { backgroundColor: alpha("#5b5b5f", 0.1) },
                    }}
                  >
                    {isGeneratingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>

            {/* Botón Gráficas */}
            <Grid item xs="auto">
              <Button
                variant="outlined"
                onClick={handleOpenGraficasModal}
                startIcon={<BarChartIcon />}
                disabled={loading}
                size="small"
                sx={{
                  display: "none",
                  borderRadius: 1,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Gráficas
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Contenedor de la tabla (scrollable) */}
        <Paper
          sx={{
            flex: 1,
            m: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            height: "500px",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={rows}
            columns={columns}
            initialState={{
              columns: {},
            }}
            columnVisibilityModel={{
              ...columnVisibilityModel,
              ...(!mostrarColumnaPrecioOriginal ? { precio: false } : {}),
              ...(!mostrarColumnaPrecioActualizado ? { precioActualizado: false } : {}),
              ...(!mostrarColumnaAbonado ? { pagado: false } : {}),
              ...(!mostrarColumnaSaldo ? { saldo: false } : {}),
              ...(!mostrarColumnaGanancia ? { ganancia: false } : {}),
              ...(!mostrarBtnVerDetalles ? { acciones: false } : {}),
            }}
            onColumnVisibilityModelChange={(newModel) => {
              setColumnVisibilityModel((prev) => {
                const merged = { ...prev, ...newModel };
                if (!mostrarColumnaPrecioOriginal) merged.precio = false;
                if (!mostrarColumnaPrecioActualizado) merged.precioActualizado = false;
                if (!mostrarColumnaAbonado) merged.pagado = false;
                if (!mostrarColumnaSaldo) merged.saldo = false;
                if (!mostrarColumnaGanancia) merged.ganancia = false;
                if (!mostrarBtnVerDetalles) merged.acciones = false;
                return merged;
              });
            }}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            paginationMode="server"
            getRowHeight={() => 35}
            headerHeight={35}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sortingOrder={["desc", "asc"]}
            sortingMode="server"
            // DataGrid usa 0-based index para páginas, por lo que se ajusta.
            onPageChange={(newPage) => setPage(newPage + 1)}
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-cell--textLeft": {
                justifyContent: "flex-start !important",
              },
              "& .MuiDataGrid-cell--textCenter": {
                justifyContent: "center !important",
              },
              "& .MuiDataGrid-cell--textRight": {
                justifyContent: "flex-end !important",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        {/* Contenedor del footer (paginación) */}
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            backgroundColor: "#ffffff",
            borderRadius: 3,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(0,0,0,0.03)",
          }}
        >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={totalAmount}
            vieneDe={"clientePaqueteDetalle"}
            onPageChange={(newPage) => setPage(newPage)}
            // Totales para clientePaqueteDetalle
            totalPrecioOriginal={totalPrecioOriginal}
            totalPrecioActualizado={totalPrecioActualizado}
            totalAbonado={totalAbonado}
            totalSaldo={totalSaldo}
            mostrarPrecioOriginalCliente={mostrarPrecioOriginalFooter}
            mostrarPrecioActualizadoCliente={mostrarPrecioActualizadoFooter}
            mostrarAbonadoCliente={mostrarAbonadoFooter}
            mostrarSaldoCliente={mostrarSaldoFooter}
          />
        </Box>
      </Box>

      {/* Modal de Crear Cliente */}
      {openModalCliente && (
        <ModalCrearActualizarEntidad
          open={openModalCliente}
          handleClose={handleCloseClienteModal}
          entidad={selectedCliente}
          idTipoEntidad={1}
        />
      )}

      {/* Modal de gráficas */}
      <GraficasDetallesVentasModal
        open={openGraficasModal}
        handleClose={handleCloseGraficasModal}
        data={datosGraficas}
        loading={loadingGraficas}
      />

      {/* Modal de detalles (mismo que en TablaCuentaCorriente) */}
      {openModalVenta && (
        <ModalDetalleClienteGrupoDestino
          open={openModalVenta}
          handleClose={handleCloseModal}
          cuentaCorriente={selectedCuentaCorriente}
        />
      )}

      {/* Modal de documentos adjuntos */}
      <ModalDocumentosAdjuntos
        open={openModalDocumentos}
        handleClose={() => {
          setOpenModalDocumentos(false);
          setAdjuntosDocumentos(null);
        }}
        adjuntosJson={adjuntosDocumentos}
        loading={loadingDocumentos}
      />
    </ThemeProvider>
  );
};

export default TablaDetallesVentas;

